import React from 'react';
import { Stack, TextField, Typography } from '@mui/material';
import { Service, SERVICE_ANSWER_LIMIT, ServiceAnswer } from '../../types';

// The questions an owner added to a service ("Car make and model"), asked when
// booking. A question that several selected services share (same wording) is
// asked once. Answers are keyed by the question's wording while editing, then
// stored on the appointment as serviceAnswers (one per question).

export type ServiceAnswerDrafts = Record<string, string>;

type BookingQuestion = {
  key: string;
  label: string;
  required: boolean;
  // The first selected service that asks it; answers are stored against it.
  serviceId: string;
  // Every selected service that asks it.
  serviceTitles: string[];
};

const questionKey = (label: string) => label.trim().toLowerCase().replace(/\s+/g, ' ');

export const servicesWithQuestions = (services: Service[] | null | undefined): Service[] =>
  (services ?? []).filter((service) => (service.questions ?? []).length > 0);

export const bookingQuestions = (services: Service[]): BookingQuestion[] => {
  const byKey = new Map<string, BookingQuestion>();
  services.forEach((service) => (service.questions ?? []).forEach((question) => {
    const key = questionKey(question.label);
    if (!key) return;
    const existing = byKey.get(key);
    if (existing) {
      existing.required = existing.required || question.required;
      if (!existing.serviceTitles.includes(service.title)) existing.serviceTitles.push(service.title);
    } else {
      byKey.set(key, {
        key, label: question.label.trim(), required: question.required, serviceId: service.id, serviceTitles: [service.title],
      });
    }
  }));
  return Array.from(byKey.values());
};

// Rebuild drafts from answers already on the appointment (e.g. after going back a step).
export const answerDraftsFrom = (services: Service[], answers: ServiceAnswer[] | undefined): ServiceAnswerDrafts => {
  const drafts: ServiceAnswerDrafts = {};
  const keys = new Set(bookingQuestions(services).map((question) => question.key));
  (answers ?? []).forEach((answer) => {
    const key = questionKey(answer.question);
    if (keys.has(key)) drafts[key] = answer.answer;
  });
  return drafts;
};

export const serviceAnswersFrom = (services: Service[], drafts: ServiceAnswerDrafts): ServiceAnswer[] =>
  bookingQuestions(services).flatMap((question) => {
    const answer = (drafts[question.key] ?? '').trim().slice(0, SERVICE_ANSWER_LIMIT);
    return answer ? [{ serviceId: question.serviceId, question: question.label, answer }] : [];
  });

export const missingRequiredAnswers = (services: Service[], drafts: ServiceAnswerDrafts): string[] =>
  bookingQuestions(services)
    .filter((question) => question.required && !(drafts[question.key] ?? '').trim())
    .map((question) => question.key);

type ServiceQuestionsFieldsProps = {
  services: Service[];
  drafts: ServiceAnswerDrafts;
  onChange: (drafts: ServiceAnswerDrafts) => void;
  // Keys from missingRequiredAnswers to flag after a submit attempt.
  missing?: string[];
  // Staff booking on a customer's behalf: required questions can be left blank.
  allRequiredOptional?: boolean;
  disabled?: boolean;
  // The customer booking shows these in their own card, which has the heading.
  hideTitle?: boolean;
};

const ServiceQuestionsFields: React.FC<ServiceQuestionsFieldsProps> = ({
  services, drafts, onChange, missing = [], allRequiredOptional = false, disabled = false, hideTitle = false,
}) => {
  const questions = bookingQuestions(services);
  if (!questions.length) return null;
  return (
    <Stack spacing={1.5}>
      {hideTitle ? null : <Typography variant="subtitle2" fontWeight={800}>A few questions</Typography>}
      {questions.map((question) => {
        const value = drafts[question.key] ?? '';
        const required = question.required && !allRequiredOptional;
        const isMissing = missing.includes(question.key) && !value.trim();
        // With several services selected, say which one a question is for unless it applies to all.
        const onlyFor = services.length > 1 && question.serviceTitles.length < services.length ?
          `For ${question.serviceTitles.join(', ')}` : undefined;
        return (
          <TextField key={question.key} size="small" fullWidth disabled={disabled}
            label={required ? question.label : `${question.label} (optional)`}
            required={required}
            value={value}
            onChange={(event) => onChange({ ...drafts, [question.key]: event.target.value })}
            inputProps={{ maxLength: SERVICE_ANSWER_LIMIT }}
            error={isMissing}
            helperText={isMissing ? 'Please answer this question.' : onlyFor} />
        );
      })}
    </Stack>
  );
};

export default ServiceQuestionsFields;
